import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { useLocale } from '../contexts/LocaleContext';
import { TRAINING_GOAL_MAX, TRAINING_GOAL_MIN, trainingGoalPeriods, trainingsCompletedThisYear } from '../data/trainingGoal';
import { changeWeeklyTarget, currentPeriod, equivalentYearlyTarget, yearlyTarget } from '../utils/trainingGoal';

/** User-defined weekly training frequency with versioned goal periods. */
export function TrainingGoalCard() {
  const {
    t,
    locale
  } = useLocale();
  const year = new Date().getFullYear();
  const [periods, setPeriods] = useState(trainingGoalPeriods);
  const current = currentPeriod(periods);
  const [draft, setDraft] = useState(current.weeklyTarget);
  const [appliedOn, setAppliedOn] = useState<Date | null>(null);
  const target = yearlyTarget(periods, year);
  const done = trainingsCompletedThisYear;
  const pct = target > 0 ? Math.min(100, Math.round(done / target * 100)) : 0;
  const dateLocale = locale === 'pt-BR' ? ptBR : undefined;
  const formatDay = (date: Date) => format(date, locale === 'pt-BR' ? "d 'de' MMM" : 'MMM d', {
    locale: dateLocale
  });
  const weeklyLabel = (n: number) => n === 1 ? t('goals.training.weeklyOne') : t('goals.training.weekly', {
    n
  });
  const apply = () => {
    const today = new Date();
    setPeriods(previous => changeWeeklyTarget(previous, draft, today));
    setAppliedOn(today);
  };
  return <Card className="p-2" data-id="element-1461" data-mp="cj7frv0vqf5i">
      <div className="flex items-baseline justify-between gap-2" data-id="element-1462" data-mp="qh4f2z6pwznv">
        <p className="min-w-0 text-[13.5px] font-medium text-deep" data-id="element-1463" data-mp="qe9wzeb6fqxm">{t('goals.training.title')}</p>
        <p className="bee-num shrink-0 text-[12px] font-bold text-gold-deep" data-id="element-1464" data-mp="md6gcplb1qvc">{pct}%</p>
      </div>

      <p className="bee-num mt-1 text-[20px] font-bold leading-tight text-deep" data-id="element-1465" data-mp="2s9snro24sfb1">
        {weeklyLabel(current.weeklyTarget)}
      </p>
      <p className="bee-num mt-[2px] text-[12px] text-muted" data-id="element-1466" data-mp="r69l6hi4i8yr">
        {t('goals.training.yearly', {
        n: equivalentYearlyTarget(current.weeklyTarget)
      })}
      </p>

      <p className="bee-num mt-2 text-[12px] font-medium text-deep" data-id="element-1467" data-mp="w6nxibqrc52l">
        {t('goals.training.progress', {
        done,
        target,
        year
      })}
      </p>
      <div className="mt-1 h-[7px] w-full overflow-hidden rounded-pill bg-sand-deep" role="progressbar" aria-valuemin={0} aria-valuemax={target} aria-valuenow={done} aria-label={t('goals.training.progress', {
      done,
      target,
      year
    })} data-id="element-1468" data-mp="163af8rnseg0e">
        <span className="block h-full rounded-pill bg-gold" style={{
        width: `${pct}%`
      }} data-id="element-1469" data-mp="1jtvr3wetppos" />
      </div>

      <div className="mt-3 border-t border-sand-line pt-2" data-id="element-1470" data-mp="1ou1ozubos8fb">
        <p className="text-[11px] font-medium text-muted" data-id="element-1471" data-mp="1ovcp9k5xay1o">{t('goals.training.change')}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2" data-id="element-1472" data-mp="1tqi9mejog4nn">
          <div className="flex items-center gap-1" data-id="element-1473" data-mp="1xv82rvmyb54v">
            <button type="button" onClick={() => setDraft(n => Math.max(TRAINING_GOAL_MIN, n - 1))} disabled={draft <= TRAINING_GOAL_MIN} aria-label={t('goals.training.decrease')} className="grid h-11 w-11 place-items-center rounded-pill border border-deep/25 text-deep transition-[border-color] duration-150 ease-brand hover:border-deep/50 disabled:opacity-40" data-id="element-1474" data-mp="3ic8xzrvb53dn">
              <MinusIcon size={16} strokeWidth={1.6} aria-hidden="true" data-id="element-1475" data-mp="5tiula4el9mu" />
            </button>
            <span className="bee-num min-w-[64px] text-center" aria-live="polite" data-id="element-1476" data-mp="wsg2r88ym3j">
              <span className="block text-[18px] font-bold leading-none text-deep" data-id="element-1477" data-mp="3s9n3iwfp07r6">{draft}</span>
              <span className="block text-[10.5px] text-muted" data-id="element-1478" data-mp="3nc3pnxmwpwzr">{t('goals.training.perWeek')}</span>
            </span>
            <button type="button" onClick={() => setDraft(n => Math.min(TRAINING_GOAL_MAX, n + 1))} disabled={draft >= TRAINING_GOAL_MAX} aria-label={t('goals.training.increase')} className="grid h-11 w-11 place-items-center rounded-pill border border-deep/25 text-deep transition-[border-color] duration-150 ease-brand hover:border-deep/50 disabled:opacity-40" data-id="element-1479" data-mp="omd8e0qaivph">
              <PlusIcon size={16} strokeWidth={1.6} aria-hidden="true" data-id="element-1480" data-mp="3qm4vbdthhfdl" />
            </button>
          </div>
          <Button variant="primary" className="ml-auto" disabled={draft === current.weeklyTarget} onClick={apply} data-id="element-1481" data-mp="n338dj3sk3cz">
            {t('goals.training.apply')}
          </Button>
        </div>
        <p className="bee-num mt-1 text-[11px] text-muted" data-id="element-1482" data-mp="dch2iglvmr7h">
          {t('goals.training.yearly', {
          n: equivalentYearlyTarget(draft)
        })}
        </p>
        {appliedOn && <p role="status" className="mt-1 text-[11px] font-medium text-soft" data-id="element-1483" data-mp="161nyb7ty52p1">
            {t('goals.training.applied', {
          date: formatDay(appliedOn)
        })}
          </p>}
      </div>

      <div className="mt-3 border-t border-sand-line pt-2" data-id="element-1484" data-mp="2vit6wl7wzfjt">
        <p className="text-[11px] font-medium text-muted" data-id="element-1485" data-mp="2vgymr94gvrgs">{t('goals.training.history')}</p>
        <ul className="mt-1" data-id="element-1486" data-mp="2qjq3f57l1hdy">
          {[...periods].reverse().map(period => <li key={period.id} className="bee-num flex items-center gap-1 py-[3px] text-[12px] text-deep" data-id="element-1487" data-mp="34a3e3fbuza6z">
              <span className={`h-[6px] w-[6px] shrink-0 rounded-pill ${period.effectiveTo ? 'border border-deep/30' : 'bg-gold'}`} aria-hidden="true" data-id="element-1488" data-mp="2cjxg7jv9ljaq" />
              {period.effectiveTo ? t('goals.training.period.closed', {
            n: period.weeklyTarget,
            from: formatDay(period.effectiveFrom),
            to: formatDay(period.effectiveTo)
          }) : t('goals.training.period.open', {
            n: period.weeklyTarget,
            from: formatDay(period.effectiveFrom)
          })}
            </li>)}
        </ul>
        <p className="mt-1 text-[11px] leading-snug text-muted" data-id="element-1489" data-mp="2ulutwfcuxrus">{t('goals.training.note')}</p>
      </div>
    </Card>;
}